import React from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import moment from 'moment';
import { Modal, Button, Date, Form, SelectHierarchyLabel, ToastContext } from '@weezevent/nacre';
import { ModerationOperators } from '../../../../models';
import { Loader } from 'semantic-ui-react';

import Style from './operatorsExportLogsModal.module.css';

const ERROR_MESSAGES = {
    INVALID_DATE: 'ticket.export.invalid_date',
    DATE_END_BEFORE: 'ticket.export.dateend_before_error',
    DATE_START_AFTER: 'ticket.export.datestart_after_error',
    SELECT_MODERATION_FLOW: 'ticket.moderation.operators.exportModal.errorMessage.select_moderation_flow'
};

const DATE_FORMAT = 'DD/MM/YYYY';

export default function OperatorsExportLogsModal({ organizationId, openExportLogsModal, setOpenExportLogsModal, moderationFlowsOptions }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const allOptionsValues = React.useMemo(() => {
        return moderationFlowsOptions.map(option => option.value);
    }, [moderationFlowsOptions]);

    const [selectedModerationFlow, setSelectedModerationFlow] = React.useState(allOptionsValues);
    const [date, setDate] = React.useState({ startDate: null, endDate: null });
    const [errors, setErrors] = React.useState({ startDate: null, endDate: null, selectedModerationFlow: null });
    const [isLoading, setIsLoading] = React.useState(false);

    const isFormValid = React.useMemo(() => !Object.values(errors).some(Boolean), [errors]);

    const onCloseModal = React.useCallback(() => {
        setOpenExportLogsModal(false);
    }, [setOpenExportLogsModal]);

    const handleSelectChange = React.useCallback(
        selected => {
            setSelectedModerationFlow(selected);
            setErrors(prevErrors => ({
                ...prevErrors,
                selectedModerationFlow: !selected.length ? t(ERROR_MESSAGES['SELECT_MODERATION_FLOW']) : null
            }));
        },
        [setSelectedModerationFlow, setErrors]
    );

    const labelSelected = React.useCallback(
        selected => {
            if (selected.length !== moderationFlowsOptions.length) {
                return t('ticket.moderation.operators.exportModal.selectCount', { count: selected.length });
            }
            return t('ticket.moderation.operators.exportModal.selectAll');
        },
        [moderationFlowsOptions]
    );

    const handleSubmit = React.useCallback(() => {
        setIsLoading(true);
        ModerationOperators.getLogs({
            organizationId,
            query: {
                flow_id: selectedModerationFlow.length === allOptionsValues.length ? null : selectedModerationFlow,
                date_after: date.startDate?.format('YYYY-MM-DD'),
                date_before: date.endDate?.add(1, 'days').format('YYYY-MM-DD')
            },
            cache: false,
            allow_cache: false
        })
            .then(({ response }) => {
                const blob = new Blob([response], { type: 'text/csv' });
                const file = window.URL.createObjectURL(blob);
                window.open(file, '_blank');
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.moderation.operators.exportModal.errorMessage.downloadError'));
            })
            .finally(() => {
                setIsLoading(false);
                onCloseModal();
            });
    }, [organizationId, selectedModerationFlow, date, errors, onCloseModal]);

    return (
        <Modal
            size="large"
            open={openExportLogsModal}
            onClose={onCloseModal}
            title={t('ticket.moderation.operators.exportModal.title')}
            submitButton={
                <Button
                    primary
                    label={isLoading ? <Loader className={Style['download-loader']} active /> : t('ticket.common.action.download')}
                    onClick={handleSubmit}
                    disabled={!isFormValid}
                />
            }
            cancelButton={<Button label={t('ticket.common.action.back')} onClick={onCloseModal} />}
        >
            <SelectHierarchyLabel
                multi
                label={t('ticket.moderation.operators.exportModal.selectLabel')}
                placeholder={t('ticket.moderation.operators.exportModal.selectPlaceholder')}
                selected={selectedModerationFlow}
                onLabelSelected={labelSelected}
                totalItems={moderationFlowsOptions.length}
                onChange={handleSelectChange}
                options={moderationFlowsOptions}
                errors={Boolean(errors.selectedModerationFlow)}
            />
            {errors.selectedModerationFlow && <Form.FieldErrors errors={[errors.selectedModerationFlow]} />}
            <div className={Style['exportDatesInputs']}>
                <ExportDateInput
                    date={date}
                    dateType="startDate"
                    label="ticket.moderation.operators.exportModal.startDateLabel"
                    errors={errors}
                    setErrors={setErrors}
                    setDate={setDate}
                />
                <ExportDateInput
                    date={date}
                    dateType="endDate"
                    label="ticket.moderation.operators.exportModal.endDateLabel"
                    errors={errors}
                    setErrors={setErrors}
                    setDate={setDate}
                />
            </div>
        </Modal>
    );
}

const ExportDateInput = ({ date, dateType, label, setErrors, setDate, errors }) => {
    const { t } = useTranslation();

    const validateDate = React.useCallback(
        (dateType, parsedDate) => {
            const { startDate, endDate } = date;
            switch (true) {
                case !parsedDate.isValid() && parsedDate !== null:
                    setDate(prevDate => ({ ...prevDate, [dateType]: null }));
                    break;
                case dateType === 'startDate' && endDate && parsedDate.isAfter(endDate):
                    setErrors(prevErrors => ({ ...prevErrors, [dateType]: t(ERROR_MESSAGES.DATE_END_BEFORE) }));
                    break;
                case dateType === 'endDate' && startDate && parsedDate.isBefore(startDate):
                    setErrors(prevErrors => ({ ...prevErrors, [dateType]: t(ERROR_MESSAGES.DATE_START_AFTER) }));
                    break;
                default:
                    setErrors(prevErrors => ({ ...prevErrors, [dateType]: null }));
                    break;
            }
        },
        [date, setErrors, setDate]
    );
    const handleDateChange = React.useCallback(
        (dateType, dateValue) => {
            const parsedDate = moment(dateValue, DATE_FORMAT, true);
            setDate(prevDate => ({ ...prevDate, [dateType]: parsedDate }));
            validateDate(dateType, parsedDate);
        },
        [validateDate]
    );

    return (
        <div>
            <Form.Label>{t(label)}</Form.Label>
            <Date.Input
                value={date[dateType]}
                locale={i18n.language}
                onChange={value => handleDateChange(dateType, value)}
                returnFormat={DATE_FORMAT}
                errors={Boolean(errors[dateType])}
            />
            {errors[dateType] && <Form.FieldErrors errors={[errors[dateType]]} />}
        </div>
    );
};
